// Iter85 — Freight Deals smart intake (full page, searchable parameter panel).
//
// Sections:
//   1. Core fields in the middle (12 most-used).
//   2. Right side panel listing ALL ~60 parameters with a search box; clicking
//      a field opens a popover bubble for value entry. Filled fields shown
//      with a green tick; clicking a filled item lets the user re-edit.
//   3. Flexible stops list (no limit) for pickup + dropoff.
//
// Light theme by default (CSS variables); reads --bg / --text / --border-strong
// from the app theme so dark mode works automatically.
import React, { useMemo, useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, Lock, Plus, Save, Search, Trash2, UserPlus, X } from "lucide-react";
import {
  LR_TYPES, useCreateLogistics, useLogisticsCategories, useCreateLogisticsCategory,
  usePatchLogistics, useLogisticsRequest, useVehicleTypes,
  useSurcharges, SURCHARGE_CATEGORIES, SURCHARGE_CATEGORY_LABELS, SURCHARGE_CATEGORY_TINT,
  LOADING_SIDES, LOADING_SIDE_LABELS,
  useTendersForLinking, uploadAttachment,
  useSurchargeTemplates, useCreateSurchargeTemplate, useDeleteSurchargeTemplate,
} from "../api/logistics";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { deriveEditScope, canEditParamKey, FIELD_GROUPS_PRICING, FIELD_GROUPS_EXECUTION } from "./logistics/editor/roleScope";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// γ-3-D refactor — PARAMS catalogue + helpers extracted to a shared module so
// the editor's LiveExtrasPanel and the detail page's AdditionalParametersPanel
// can share one source of truth.
import { PARAMS, PARAM_GROUPS, PARAM_GROUP_SHORT, PARAM_GROUP_LABELS_I18N, isFilled, PARAM_BY_KEY } from "./logistics/editor/params";



// ─────────────────────── Stop component ───────────────────────

const emptyStop = (kind = "pickup") => ({
  kind,
  country: "", postal_code: "", city: "", street: "", house_number: "",
  entry_gate: "", dock_number: "",
  // iter89 γ-2 #1 — merged "When" range + per-stop firmness. date_time is
  // kept as a legacy mirror of window_start_at so older PDFs/Excel still
  // render correctly.
  date_time: "", window_start_at: "", window_end_at: "", priority: "",
  time_window: "",  // legacy free-text — hidden in UI, preserved on edit
  customs: false,
  // iter89 γ-2 #2 — per-stop loading duration + free-text notes
  loading_minutes: "", loading_notes: "",
  // iter91 γ-3-A — loading sides multi-select (rear / left / right / top_crane / both_sides / other)
  loading_sides: [],
  // iter88 γ-2 #5 — per-stop cargo
  aru_megnevezese: "", suly_kg: "", rakmeter: "", kobmeter: "",
  // iter88 γ-2 #6 — returnable packaging items (combined stops only)
  gongyoleg_items: [],
});

const emptyGongyoleg = () => ({ aru_megnevezese: "", suly_kg: "", rakmeter: "", kobmeter: "" });

const KIND_LABELS = {
  pickup:         { label: "Felrakó",          color: "#185FA5" },
  dropoff_pickup: { label: "Lerakó + Felrakó", color: "#7e3aa8" },
  dropoff:        { label: "Lerakó",           color: "#22a35a" },
};

const PRIORITY_OPTS = [
  { v: "",       label: "—",          color: "#6b7388" },
  { v: "fixed",  label: "Fixed slot", color: "#b22a29" },
  { v: "flex",   label: "Flex",       color: "#22a35a" },
  { v: "asap",   label: "ASAP",       color: "#b7770d" },
];

// Pickup stops can be Pickup or Dropoff+Pickup (intermediate combined stop).
// Dropoff stops can be Dropoff or Dropoff+Pickup. This lets users define
// chained round-trips without inventing a separate "intermediate stops" list.
const KIND_CHOICES = {
  pickup:  ["pickup", "dropoff_pickup"],
  dropoff: ["dropoff", "dropoff_pickup"],
};

const StopRow = ({ stop, idx, prefix, onChange, onRemove, onCopyFromPrev, canCopyFromPrev }) => {
  const kindMeta = KIND_LABELS[stop.kind || (prefix === "pickup" ? "pickup" : "dropoff")] || KIND_LABELS.pickup;
  const isCombined = (stop.kind || "") === "dropoff_pickup";
  const choices = KIND_CHOICES[prefix] || ["pickup"];
  // Mirror window_start_at <-> date_time on change so backend mirror stays
  // consistent even if the user hasn't typed the start yet.
  const setWindowStart = (v) => { onChange("window_start_at", v); onChange("date_time", v); };
  const setGongyoleg = (i, key, value) => {
    const arr = [...(stop.gongyoleg_items || [])];
    arr[i] = { ...arr[i], [key]: value };
    onChange("gongyoleg_items", arr);
  };
  const addGongyoleg = () => onChange("gongyoleg_items", [...(stop.gongyoleg_items || []), emptyGongyoleg()]);
  const removeGongyoleg = (i) => {
    const arr = [...(stop.gongyoleg_items || [])];
    arr.splice(i, 1);
    onChange("gongyoleg_items", arr);
  };
  return (
    <div className="rounded p-3 mb-2" style={{ background: "var(--bg-surface, #fbfcfe)", border: `1px solid ${kindMeta.color}33`, borderLeft: `3px solid ${kindMeta.color}` }} data-testid={`${prefix}-stop-${idx}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 9999, background: kindMeta.color }} aria-hidden="true" />
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "var(--text-primary, #0d0f18)" }} data-testid={`${prefix}-stop-${idx}-kind-label`}>
            {kindMeta.label} #{idx + 1}
          </span>
          {/* γ-2 #6 — kind segmented control. Only render when there is a real
              choice (i.e. more than one allowed kind for this stop list). */}
          {choices.length > 1 && (
            <div className="inline-flex rounded overflow-hidden" style={{ border: "1px solid rgba(0,0,0,0.15)", marginLeft: 6 }} data-testid={`${prefix}-stop-${idx}-kind-picker`}>
              {choices.map((c) => {
                const active = (stop.kind || (prefix === "pickup" ? "pickup" : "dropoff")) === c;
                const meta = KIND_LABELS[c];
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => onChange("kind", c)}
                    className="px-2 py-0.5"
                    style={{
                      background: active ? meta.color : "transparent",
                      color: active ? "#ffffff" : meta.color,
                      border: "none",
                      borderLeft: c !== choices[0] ? "1px solid rgba(0,0,0,0.15)" : "none",
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: "0.4px",
                      cursor: "pointer",
                    }}
                    data-testid={`${prefix}-stop-${idx}-kind-${c}`}
                  >
                    {c === "pickup" ? "Felrakó" : c === "dropoff" ? "Lerakó" : "Lerakó+Felrakó"}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {canCopyFromPrev && (
            <button type="button" onClick={onCopyFromPrev} className="px-2 py-1 rounded inline-flex items-center gap-1" style={{ background: "transparent", border: "1px solid #185FA5", color: "#185FA5", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, cursor: "pointer" }} title="Copy cargo from previous stop" data-testid={`${prefix}-stop-${idx}-copy-prev`}>
              Copy cargo ↑
            </button>
          )}
          {onRemove && (
            <button type="button" onClick={onRemove} className="px-2 py-1 rounded inline-flex items-center gap-1" style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.45)", color: "#b22a29", fontFamily: "'DM Mono', monospace", fontSize: 10, cursor: "pointer" }} data-testid={`${prefix}-stop-${idx}-remove`}>
              <Trash2 size={11} /> Remove
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <FieldLite label="Country *"><Input value={stop.country} onChange={(v) => onChange("country", v)} testid={`${prefix}-stop-${idx}-country`} /></FieldLite>
        <FieldLite label="Postal code *"><Input value={stop.postal_code} onChange={(v) => onChange("postal_code", v)} testid={`${prefix}-stop-${idx}-postal`} /></FieldLite>
        <FieldLite label="City *"><Input value={stop.city} onChange={(v) => onChange("city", v)} testid={`${prefix}-stop-${idx}-city`} /></FieldLite>
        <FieldLite label="Utca *"><Input value={stop.street} onChange={(v) => onChange("street", v)} testid={`${prefix}-stop-${idx}-street`} /></FieldLite>
        <FieldLite label="Házszám *"><Input value={stop.house_number} onChange={(v) => onChange("house_number", v)} testid={`${prefix}-stop-${idx}-house`} /></FieldLite>
        <FieldLite label="Behajtási kapu"><Input value={stop.entry_gate} onChange={(v) => onChange("entry_gate", v)} testid={`${prefix}-stop-${idx}-gate`} /></FieldLite>
        <FieldLite label="Dokk-szám"><Input value={stop.dock_number} onChange={(v) => onChange("dock_number", v)} testid={`${prefix}-stop-${idx}-dock`} /></FieldLite>
        <label className="inline-flex items-center gap-2 mt-5" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text, #0d0f18)" }}>
          <input type="checkbox" checked={!!stop.customs} onChange={(e) => onChange("customs", e.target.checked)} data-testid={`${prefix}-stop-${idx}-customs`} /> Vámkezelés
        </label>
      </div>

      {/* γ-2 #1 — merged "When" range (window_start_at → window_end_at) + per-stop priority */}
      <div className="mt-3 pt-3" style={{ borderTop: "1px dashed var(--border-soft, rgba(0,0,0,0.08))" }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--text-dimmer, #6b7388)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 6 }}>
          When (window)
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 items-end">
          <FieldLite label="From"><Input type="datetime-local" value={(stop.window_start_at || stop.date_time || "").slice(0, 16)} onChange={setWindowStart} testid={`${prefix}-stop-${idx}-window-start`} /></FieldLite>
          <FieldLite label="To"><Input type="datetime-local" value={(stop.window_end_at || "").slice(0, 16)} onChange={(v) => onChange("window_end_at", v)} testid={`${prefix}-stop-${idx}-window-end`} /></FieldLite>
          <FieldLite label="Firmness">
            <div className="inline-flex rounded overflow-hidden" style={{ border: "1px solid rgba(0,0,0,0.15)" }} data-testid={`${prefix}-stop-${idx}-priority-picker`}>
              {PRIORITY_OPTS.map((opt, i) => {
                const active = (stop.priority || "") === opt.v;
                return (
                  <button
                    key={opt.v || "_"}
                    type="button"
                    onClick={() => onChange("priority", opt.v)}
                    className="px-2 py-1"
                    style={{
                      background: active ? opt.color : "transparent",
                      color: active ? "#ffffff" : opt.color,
                      border: "none",
                      borderLeft: i > 0 ? "1px solid rgba(0,0,0,0.10)" : "none",
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: "0.4px",
                      cursor: "pointer",
                    }}
                    data-testid={`${prefix}-stop-${idx}-priority-${opt.v || "none"}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </FieldLite>
        </div>
      </div>

      {/* γ-2 #2 — loading duration + free-text notes (gate code, lift-gate needed, etc.) */}
      <div className="mt-3 pt-3" style={{ borderTop: "1px dashed var(--border-soft, rgba(0,0,0,0.08))" }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--text-dimmer, #6b7388)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 6 }}>
          Loading / unloading
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-2 items-end">
          <FieldLite label="Duration (min)"><Input type="number" value={stop.loading_minutes ?? ""} onChange={(v) => onChange("loading_minutes", v === "" ? null : Number(v))} testid={`${prefix}-stop-${idx}-loading-minutes`} /></FieldLite>
          <FieldLite label="Method / non-standard reqs (gate code, lift-gate, etc.)">
            <Input value={stop.loading_notes || ""} onChange={(v) => onChange("loading_notes", v)} placeholder="e.g. gate code 1234, lift-gate required, side-loading only" testid={`${prefix}-stop-${idx}-loading-notes`} />
          </FieldLite>
        </div>
        {/* γ-3-A — loading sides multi-select */}
        <div className="mt-2">
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--text-dimmer, #6b7388)", letterSpacing: "0.5px", marginBottom: 4 }}>Loading side (multi-select)</div>
          <LoadingSidesPicker value={stop.loading_sides || []} onChange={(v) => onChange("loading_sides", v)} testidPrefix={`${prefix}-stop-${idx}-loading-side`} />
        </div>
      </div>

      {/* iter88 γ-2 #5 — per-stop cargo */}
      <div className="mt-3 pt-3" style={{ borderTop: "1px dashed var(--border-soft, rgba(0,0,0,0.08))" }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--text-dimmer, #6b7388)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 6 }}>Cargo at this stop</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <FieldLite label="Áru megnevezése"><Input value={stop.aru_megnevezese || ""} onChange={(v) => onChange("aru_megnevezese", v)} testid={`${prefix}-stop-${idx}-cargo-name`} /></FieldLite>
          <FieldLite label="Súly (kg)"><Input type="number" value={stop.suly_kg ?? ""} onChange={(v) => onChange("suly_kg", v === "" ? null : Number(v))} testid={`${prefix}-stop-${idx}-cargo-weight`} /></FieldLite>
          <FieldLite label="LDM"><Input type="number" value={stop.rakmeter ?? ""} onChange={(v) => onChange("rakmeter", v === "" ? null : Number(v))} testid={`${prefix}-stop-${idx}-cargo-ldm`} /></FieldLite>
          <FieldLite label="Köbméter"><Input type="number" value={stop.kobmeter ?? ""} onChange={(v) => onChange("kobmeter", v === "" ? null : Number(v))} testid={`${prefix}-stop-${idx}-cargo-m3`} /></FieldLite>
        </div>
      </div>

      {/* iter88 γ-2 #6 — Göngyöleg items only for combined dropoff_pickup stops */}
      {isCombined && (
        <div className="mt-3 pt-3" style={{ borderTop: "1px dashed var(--border-soft, rgba(0,0,0,0.08))" }} data-testid={`${prefix}-stop-${idx}-gongyoleg-section`}>
          <div className="flex items-center justify-between mb-2">
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#7e3aa8", letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 700 }}>
              Göngyöleg (returnable packaging) picked up here
            </div>
            <button type="button" onClick={addGongyoleg} className="px-2 py-1 rounded inline-flex items-center gap-1" style={{ background: "rgba(126,58,168,0.10)", border: "1px solid #7e3aa8", color: "#7e3aa8", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, cursor: "pointer" }} data-testid={`${prefix}-stop-${idx}-gongyoleg-add`}>+ Add item</button>
          </div>
          {(stop.gongyoleg_items || []).length === 0 && (
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer, #6b7388)", padding: 6, textAlign: "center" }}>No göngyöleg items yet</div>
          )}
          {(stop.gongyoleg_items || []).map((g, gi) => (
            <div key={gi} className="grid grid-cols-[1.6fr_0.8fr_0.6fr_0.6fr_auto] gap-2 items-end mb-1.5" data-testid={`${prefix}-stop-${idx}-gongyoleg-${gi}`}>
              <FieldLite label={`Áru #${gi + 1}`}><Input value={g.aru_megnevezese || ""} onChange={(v) => setGongyoleg(gi, "aru_megnevezese", v)} testid={`${prefix}-stop-${idx}-gongyoleg-${gi}-name`} /></FieldLite>
              <FieldLite label="Súly (kg)"><Input type="number" value={g.suly_kg ?? ""} onChange={(v) => setGongyoleg(gi, "suly_kg", v === "" ? null : Number(v))} testid={`${prefix}-stop-${idx}-gongyoleg-${gi}-weight`} /></FieldLite>
              <FieldLite label="LDM"><Input type="number" value={g.rakmeter ?? ""} onChange={(v) => setGongyoleg(gi, "rakmeter", v === "" ? null : Number(v))} testid={`${prefix}-stop-${idx}-gongyoleg-${gi}-ldm`} /></FieldLite>
              <FieldLite label="m³"><Input type="number" value={g.kobmeter ?? ""} onChange={(v) => setGongyoleg(gi, "kobmeter", v === "" ? null : Number(v))} testid={`${prefix}-stop-${idx}-gongyoleg-${gi}-m3`} /></FieldLite>
              <button type="button" onClick={() => removeGongyoleg(gi)} className="px-2 py-1 rounded" style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.45)", color: "#b22a29", cursor: "pointer" }} data-testid={`${prefix}-stop-${idx}-gongyoleg-${gi}-remove`}><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────── Editor page ───────────────────────

export default function LogisticsEditor() {
  const { id } = useParams();
  const editing = !!id && id !== "new";
  const existing = useLogisticsRequest(editing ? id : null);
  const cats = useLogisticsCategories();
  const newCat = useCreateLogisticsCategory();
  const create = useCreateLogistics();
  const patch = usePatchLogistics();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  // γ-3-G — Role-based edit scope. For new deals (no existing.data yet) the
  // user is the registrant ⇒ effectively handler scope with pricing unlocked.
  // Updates as soon as the deal hydrates from the backend.
  const editScope = deriveEditScope(me, existing.data || null);
  const dealStage = existing.data?.deal_stage || "registered";

  const [form, setForm] = useState(() => ({
    deal_name: "", request_type: "tender", customer_name: "", customer_category_id: "",
    currency: "EUR", celar: "", leadasi_hatarido: "",
    suly_kg: "", rakmeter: "", kobmeter: "", aru_megnevezese: "", autotipus: "",
    is_tender: true,
    pickups: [emptyStop()],
    dropoffs: [emptyStop("dropoff")],
    felrako_orszagok: [], lerako_orszagok: [],  // derived from stops by backend (iter87 γ-1 fix #1) — kept here only so empty load doesn't crash
    korfuvar: false, korfuvar_eltero_felrako: false, korfuvar_eltero_lerako: false,
    vamkezeles_felrako: false, vamkezeles_lerako: false,
    priority_side: "",  // iter89 γ-2 #1 — which side's timing is binding
    // iter91 γ-3-A
    vehicle_delay_flag: false,
    surcharges: [],
    customer_tenant_id: "",
    payment_term: "",
    // I.3 — Contract-rate match key fields + deviation justification
    load_type: "",
    deviation_justification: "",
    frame_order_id: "",
    // iter93 γ-3-C — Tender linking on creation
    linked_tender_id: "",
    linked_tender_external_ref: "",
    linked_tender_note: "",
    extra: {}, // holds all the side-panel values
  }));

  const seededRef = useRef(false);
  React.useEffect(() => {
    if (!editing || !existing.data || seededRef.current) return;
    seededRef.current = true;
    const d = existing.data;
    const extra = {};
    PARAMS.forEach((p) => { if (d[p.k] != null) extra[p.k] = d[p.k]; });
    setForm({
      deal_name: d.deal_name || "",
      request_type: d.request_type || "tender",
      customer_name: d.customer_name || "",
      customer_category_id: d.customer_category_id || "",
      currency: d.currency || "EUR",
      celar: d.celar ?? "",
      leadasi_hatarido: (d.leadasi_hatarido || "").slice(0, 16),
      suly_kg: d.suly_kg ?? "",
      rakmeter: d.rakmeter ?? "",
      kobmeter: d.kobmeter ?? "",
      aru_megnevezese: d.aru_megnevezese || "",
      autotipus: d.autotipus || "",
      is_tender: !!d.is_tender,
      pickups: (d.pickups || []).length ? d.pickups : [emptyStop()],
      dropoffs: (d.dropoffs || []).length ? d.dropoffs : [emptyStop("dropoff")],
      felrako_orszagok: d.felrako_orszagok || [],
      lerako_orszagok: d.lerako_orszagok || [],
      korfuvar: !!d.korfuvar,
      korfuvar_eltero_felrako: !!d.korfuvar_eltero_felrako,
      korfuvar_eltero_lerako: !!d.korfuvar_eltero_lerako,
      vamkezeles_felrako: !!d.vamkezeles_felrako,
      vamkezeles_lerako: !!d.vamkezeles_lerako,
      priority_side: d.priority_side || "",
      vehicle_delay_flag: !!d.vehicle_delay_flag,
      surcharges: d.surcharges || [],
      customer_tenant_id: d.customer_tenant_id || "",
      payment_term: d.payment_term || "",
      load_type: d.load_type || "",
      deviation_justification: d.deviation_justification || "",
      frame_order_id: d.frame_order_id || "",
      linked_tender_id: d.linked_tender_id || "",
      linked_tender_external_ref: d.linked_tender_external_ref || "",
      linked_tender_note: d.linked_tender_note || "",
      extra,
    });
  }, [existing.data, editing]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setExtra = (k, v) => setForm((s) => ({ ...s, extra: { ...s.extra, [k]: v } }));
  const clearExtra = (k) => setForm((s) => { const e = { ...s.extra }; delete e[k]; return { ...s, extra: e }; });

  const updateStop = (which, idx, k, v) => setForm((s) => {
    const arr = [...s[which]];
    arr[idx] = { ...arr[idx], [k]: v };
    return { ...s, [which]: arr };
  });
  const addStop = (which) => setForm((s) => ({ ...s, [which]: [...s[which], emptyStop(which === "pickups" ? "pickup" : "dropoff")] }));
  const removeStop = (which, idx) => setForm((s) => {
    const arr = [...s[which]];
    arr.splice(idx, 1);
    return { ...s, [which]: arr.length ? arr : [emptyStop(which === "pickups" ? "pickup" : "dropoff")] };
  });

  // γ-2 — copy cargo (description + weight + LDM + m³) from the previous stop
  // in the SAME list (pickups[] or dropoffs[]). Saves typing for routes where
  // every stop carries the same cargo. Göngyöleg is NOT copied — those are
  // unique per round-trip stop.
  const copyCargoFromPrev = (which, idx) => setForm((s) => {
    if (idx <= 0) return s;
    const arr = [...s[which]];
    const prev = arr[idx - 1] || {};
    arr[idx] = {
      ...arr[idx],
      aru_megnevezese: prev.aru_megnevezese || "",
      suly_kg: prev.suly_kg ?? "",
      rakmeter: prev.rakmeter ?? "",
      kobmeter: prev.kobmeter ?? "",
    };
    return { ...s, [which]: arr };
  });

  const [err, setErr] = useState("");

  // γ-2 — sanitize stops before submit: numeric fields (loading_minutes,
  // suly_kg, rakmeter, kobmeter) come out of <input type="number"> as "" when
  // the user leaves them blank. Pydantic v2 rejects "" for int/float so we
  // coerce "" → null at the boundary. Same treatment for göngyöleg items.
  const _NUMERIC_STOP_KEYS = ["loading_minutes", "suly_kg", "rakmeter", "kobmeter"];
  const _NUMERIC_GONGY_KEYS = ["suly_kg", "rakmeter", "kobmeter"];
  const sanitizeStop = (s) => {
    const out = { ...s };
    _NUMERIC_STOP_KEYS.forEach((k) => { if (out[k] === "") out[k] = null; });
    out.gongyoleg_items = (s.gongyoleg_items || []).map((g) => {
      const og = { ...g };
      _NUMERIC_GONGY_KEYS.forEach((k) => { if (og[k] === "") og[k] = null; });
      return og;
    });
    return out;
  };

  const submit = () => {
    setErr("");
    if (!form.deal_name.trim()) { setErr("Deal name is required"); return; }
    // Validate stop required fields
    const stopMissing = (arr, label) => arr.some((s) => !(s.country && s.postal_code && s.city && s.street && s.house_number));
    if (stopMissing(form.pickups, "Felrakó") || stopMissing(form.dropoffs, "Lerakó")) {
      setErr("Every stop needs country, postal code, city, utca, házszám"); return;
    }
    const body = {
      deal_name: form.deal_name.trim(),
      request_type: form.request_type,
      customer_name: form.customer_name.trim() || null,
      customer_category_id: form.customer_category_id || null,
      currency: form.currency,
      celar: form.celar === "" ? null : Number(form.celar),
      leadasi_hatarido: form.leadasi_hatarido || null,
      suly_kg: form.suly_kg === "" ? null : Number(form.suly_kg),
      rakmeter: form.rakmeter === "" ? null : Number(form.rakmeter),
      kobmeter: form.kobmeter === "" ? null : Number(form.kobmeter),
      aru_megnevezese: form.aru_megnevezese.trim() || null,
      autotipus: form.autotipus.trim() || null,
      is_tender: form.is_tender || form.request_type === "tender",
      pickups: form.pickups.map(sanitizeStop),
      dropoffs: form.dropoffs.map(sanitizeStop),
      // felrako_orszagok / lerako_orszagok intentionally omitted — derived
      // server-side from stops (iter87 γ-1 fix #1).
      korfuvar: form.korfuvar,
      korfuvar_eltero_felrako: form.korfuvar_eltero_felrako,
      korfuvar_eltero_lerako: form.korfuvar_eltero_lerako,
      vamkezeles_felrako: form.vamkezeles_felrako,
      vamkezeles_lerako: form.vamkezeles_lerako,
      priority_side: form.priority_side || null,
      vehicle_delay_flag: !!form.vehicle_delay_flag,
      surcharges: form.surcharges || [],
      customer_tenant_id: form.customer_tenant_id || null,
      payment_term: (form.payment_term || "").trim() || null,
      load_type: (form.load_type || "").trim() || null,
      deviation_justification: (form.deviation_justification || "").trim() || null,
      frame_order_id: (form.frame_order_id || "").trim() || null,
      linked_tender_id: form.linked_tender_id || null,
      linked_tender_external_ref: form.linked_tender_external_ref || null,
      linked_tender_note: form.linked_tender_note || null,
      ...form.extra,
    };
    if (editing) {
      patch.mutate({ id, body }, { onSuccess: () => navigate(`/logistics-requests/${id}`), onError: (e) => setErr(formatErr(e)) });
    } else {
      create.mutate(body, { onSuccess: (d) => navigate(`/logistics-requests/${d.id}`), onError: (e) => setErr(formatErr(e)) });
    }
  };

  const busy = create.isPending || patch.isPending;

  return (
    <div className="px-6 py-6" data-testid="logistics-editor-page" style={{ background: "var(--bg, #f6f7fb)", color: "var(--text, #0d1220)", minHeight: "100vh" }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate("/logistics-requests")} className="inline-flex items-center gap-1 px-2 py-1 rounded" style={btnGhost} data-testid="logistics-editor-back">
            <ArrowLeft size={13} /> Back
          </button>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 22 }}>
            {editing ? `${editScope.buttonLabel || "Edit"} ${id}` : "New Freight Deal"}
          </h1>
          {/* γ-3-G — Scope banner. Tells the actor at-a-glance which field
              group they're allowed to write. Same detail page, different
              writable surface — keeps context (route/cargo/customer) visible
              to pricers while they edit only pricing fields. */}
          {editing && editScope.scope !== "admin" && editScope.scope !== "both" && (
            <span
              data-testid="logistics-editor-scope-badge"
              title={editScope.scope === "pricer" ? "You're the assigned Árazó — only pricing fields are writable" : "You're the assigned Handler — pricing fields are read-only"}
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 9,
                fontWeight: 700,
                color: editScope.scope === "pricer" ? "#7e3aa8" : "#185FA5",
                background: editScope.scope === "pricer" ? "rgba(126,58,168,0.10)" : "rgba(24,95,165,0.10)",
                border: `1px solid ${editScope.scope === "pricer" ? "rgba(126,58,168,0.40)" : "rgba(24,95,165,0.40)"}`,
                padding: "2px 8px",
                borderRadius: 3,
                letterSpacing: "0.6px",
                textTransform: "uppercase",
              }}
            >
              {editScope.scope === "pricer" ? "Árazás scope · pricing-only" : "Handler scope · pricing locked"}
            </span>
          )}
        </div>
        <button type="button" onClick={submit} disabled={busy} className="inline-flex items-center gap-2 px-3 py-2 rounded" style={btnPrimary(busy)} data-testid="logistics-editor-save">
          <Save size={13} /> {editing ? "Save changes" : "Create deal"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        {/* CENTER — core fields + stops */}
        <div className="space-y-4">
          {err && <div className="px-3 py-2 rounded" style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.55)", color: "#b22a29", fontFamily: "'DM Mono', monospace", fontSize: 12 }} data-testid="logistics-editor-error">{err}</div>}
          {/* γ-3-G — Core / Route / Stops / KPI flags / Tender are gated by
              the actor's scope. Pricers see this entire block as read-only
              context (route/cargo/customer) while their writable surface is
              the Pricing + Surcharges + side-panel pricing items. */}
          <LockedFieldset
            disabled={!editScope.flags.core && !editScope.flags.route && !editScope.flags.stops}
            reason="Read-only for the Árazó scope — context only"
            testid="lr-core-scope-lock"
          >
          <Panel title="Core">
            <FieldLite label="Deal name *"><Input value={form.deal_name} onChange={(v) => set("deal_name", v)} testid="lr-deal-name" /></FieldLite>
            <div className="grid grid-cols-3 gap-2">
              <FieldLite label="Request type *">
                <Select value={form.request_type} onChange={(v) => set("request_type", v)} options={LR_TYPES.map((t) => [t, t])} testid="lr-request-type" />
              </FieldLite>
              <FieldLite label="Currency">
                <Select value={form.currency} onChange={(v) => set("currency", v)} options={[["EUR", "EUR"], ["USD", "USD"], ["HUF", "HUF"], ["GBP", "GBP"]]} testid="lr-currency" />
              </FieldLite>
              <FieldLite label="Leadási határidő"><Input type="datetime-local" value={form.leadasi_hatarido} onChange={(v) => set("leadasi_hatarido", v)} testid="lr-leadasi-hatarido" /></FieldLite>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FieldLite label="Customer name"><Input value={form.customer_name} onChange={(v) => set("customer_name", v)} testid="lr-customer-name" /></FieldLite>
              <FieldLite label="Customer category">
                <CategorySelect value={form.customer_category_id} onChange={(v) => set("customer_category_id", v)} cats={cats.data?.items || []} onCreate={(name) => newCat.mutate({ name })} />
              </FieldLite>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FieldLite label="Customer tenant id (for templates / payment-term lookup)">
                <Input value={form.customer_tenant_id} onChange={(v) => set("customer_tenant_id", v)} testid="lr-customer-tenant-id" placeholder="optional — paste a customer tenant_id" />
              </FieldLite>
              <FieldLite label="Payment term *">
                <PaymentTermSelect value={form.payment_term} onChange={(v) => set("payment_term", v)} customerTenantId={form.customer_tenant_id} />
              </FieldLite>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FieldLite label="Load type (FTL/LTL/FCL/LCL · matches contract rate card)">
                <Select value={form.load_type} onChange={(v) => set("load_type", v)} options={[["", "—"], ["FTL", "FTL"], ["LTL", "LTL"], ["FCL", "FCL"], ["LCL", "LCL"], ["TRACTOR_ONLY", "Tractor only"], ["GROUPAGE", "Groupage"]]} testid="lr-load-type" />
              </FieldLite>
              <FieldLite label="Deviation justification (required when price ≠ contract rate)">
                <Input value={form.deviation_justification} onChange={(v) => set("deviation_justification", v)} testid="lr-deviation-justification" placeholder="e.g. competitor undercut; one-off lane test" />
              </FieldLite>
            </div>
            <div className="grid grid-cols-1 gap-2">
              <FieldLite label="Frame / Blanket order id (J.2 — call-off draws down its caps)">
                <Input value={form.frame_order_id} onChange={(v) => set("frame_order_id", v)} testid="lr-frame-order-id" placeholder="optional — paste a frame_order_id; LR draws down on save" />
              </FieldLite>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FieldLite label="Áru megnevezése"><Input value={form.aru_megnevezese} onChange={(v) => set("aru_megnevezese", v)} testid="lr-aru" /></FieldLite>
              <FieldLite label="Autótípus"><VehicleTypeSelect value={form.autotipus} onChange={(v) => set("autotipus", v)} /></FieldLite>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <FieldLite label="Súly (kg)"><Input type="number" value={form.suly_kg} onChange={(v) => set("suly_kg", v)} testid="lr-weight" /></FieldLite>
              <FieldLite label="LDM"><Input type="number" value={form.rakmeter} onChange={(v) => set("rakmeter", v)} testid="lr-ldm" /></FieldLite>
              <FieldLite label="Köbméter"><Input type="number" value={form.kobmeter} onChange={(v) => set("kobmeter", v)} testid="lr-volume" /></FieldLite>
              <FieldLite label="Célár"><Input type="number" value={form.celar} onChange={(v) => set("celar", v)} testid="lr-celar" /></FieldLite>
            </div>
          </Panel>

          <Panel title="Route">
            {/* iter95c γ-3-E — Round-trip detected banner. Auto-shows when any
                stop is `dropoff_pickup` (the chained round-trip pattern). Offers a
                one-click shortcut to "Mirror first pickup as return pickup" so
                users don't have to retype the same cargo/address. */}
            {((form.pickups || []).some((s) => s.kind === "dropoff_pickup") ||
              (form.dropoffs || []).some((s) => s.kind === "dropoff_pickup")) && (
              <div
                className="mb-3 px-3 py-2 rounded flex items-start gap-2 flex-wrap"
                style={{ background: "rgba(126,58,168,0.08)", border: "1px solid rgba(126,58,168,0.40)", borderLeft: "3px solid #7e3aa8" }}
                data-testid="lr-roundtrip-banner"
              >
                <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 11, color: "#7e3aa8", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                  Round-trip detected
                </span>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer, #6b7388)", flex: 1, minWidth: 200 }}>
                  At least one stop is a combined dropoff+pickup. Make sure göngyöleg items are filled at the combined stop.
                </span>
                <button
                  type="button"
                  onClick={() => {
                    // Add a return pickup that mirrors the FIRST pickup's
                    // address + cargo. Saves the user the repeat typing for
                    // the most common round-trip shape (out-and-back).
                    const first = (form.pickups || [])[0];
                    if (!first) return;
                    setForm((s) => ({
                      ...s,
                      pickups: [...s.pickups, {
                        ...emptyStop("pickup"),
                        country: first.country, postal_code: first.postal_code,
                        city: first.city, street: first.street, house_number: first.house_number,
                        aru_megnevezese: first.aru_megnevezese,
                        suly_kg: first.suly_kg, rakmeter: first.rakmeter, kobmeter: first.kobmeter,
                      }],
                    }));
                  }}
                  className="px-2 py-1 rounded"
                  style={{ background: "#7e3aa8", color: "#ffffff", border: "1px solid #4f1e6a", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.4px", cursor: "pointer" }}
                  data-testid="lr-roundtrip-add-return"
                  title="Add a return pickup that mirrors the first pickup's address + cargo"
                >
                  + Add return pickup
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-3 mb-3">
              <Toggle label="Körfuvar" checked={form.korfuvar} onChange={(v) => set("korfuvar", v)} testid="lr-korfuvar" />
              <Toggle label="Eltérő felrakó" checked={form.korfuvar_eltero_felrako} onChange={(v) => set("korfuvar_eltero_felrako", v)} testid="lr-korfuvar-felrako" />
              <Toggle label="Eltérő lerakó" checked={form.korfuvar_eltero_lerako} onChange={(v) => set("korfuvar_eltero_lerako", v)} testid="lr-korfuvar-lerako" />
              <Toggle label="Vámkezelés felrakó" checked={form.vamkezeles_felrako} onChange={(v) => set("vamkezeles_felrako", v)} testid="lr-vam-felrako" />
              <Toggle label="Vámkezelés lerakó" checked={form.vamkezeles_lerako} onChange={(v) => set("vamkezeles_lerako", v)} testid="lr-vam-lerako" />
            </div>
            {/* γ-2 #1 — deal-level priority side. Which end's timing matters most? */}
            <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="lr-priority-side-row">
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer, #6b7388)", letterSpacing: "1px", textTransform: "uppercase" }}>Priority side</span>
              <div className="inline-flex rounded overflow-hidden" style={{ border: "1px solid rgba(0,0,0,0.15)" }}>
                {[
                  { v: "", label: "—" },
                  { v: "pickup", label: "Pickup-critical" },
                  { v: "dropoff", label: "Dropoff-critical" },
                  { v: "both", label: "Both" },
                ].map((opt, i) => {
                  const active = (form.priority_side || "") === opt.v;
                  return (
                    <button
                      key={opt.v || "_"}
                      type="button"
                      onClick={() => set("priority_side", opt.v)}
                      className="px-3 py-1"
                      style={{
                        background: active ? "#185FA5" : "transparent",
                        color: active ? "#ffffff" : "#185FA5",
                        border: "none",
                        borderLeft: i > 0 ? "1px solid rgba(0,0,0,0.10)" : "none",
                        fontFamily: "'DM Mono', monospace",
                        fontSize: 10,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                      data-testid={`lr-priority-side-${opt.v || "none"}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Pickups (felrakó)</div>
              {form.pickups.map((s, i) => (
                <StopRow
                  key={`p${i}`}
                  stop={s}
                  idx={i}
                  prefix="pickup"
                  onChange={(k, v) => updateStop("pickups", i, k, v)}
                  onRemove={form.pickups.length > 1 ? () => removeStop("pickups", i) : null}
                  onCopyFromPrev={() => copyCargoFromPrev("pickups", i)}
                  canCopyFromPrev={i > 0}
                />
              ))}
              <button type="button" onClick={() => addStop("pickups")} className="inline-flex items-center gap-1 px-2 py-1 rounded" style={btnSecondary} data-testid="lr-add-pickup">
                <Plus size={11} /> Add pickup
              </button>
            </div>
            <div className="mt-3">
              <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Dropoffs (lerakó)</div>
              {form.dropoffs.map((s, i) => (
                <StopRow
                  key={`d${i}`}
                  stop={s}
                  idx={i}
                  prefix="dropoff"
                  onChange={(k, v) => updateStop("dropoffs", i, k, v)}
                  onRemove={form.dropoffs.length > 1 ? () => removeStop("dropoffs", i) : null}
                  onCopyFromPrev={() => copyCargoFromPrev("dropoffs", i)}
                  canCopyFromPrev={i > 0}
                />
              ))}
              <button type="button" onClick={() => addStop("dropoffs")} className="inline-flex items-center gap-1 px-2 py-1 rounded" style={btnSecondary} data-testid="lr-add-dropoff">
                <Plus size={11} /> Add dropoff
              </button>
            </div>
          </Panel>
          </LockedFieldset>

          {/* iter95b — Live "Filled from side panel" surface. Mirrors what
              the detail page will show, but lives INSIDE the editor and
              updates immediately as the user fills values via the right-
              side popovers. Lets the user see what they've already entered
              without having to scroll back into the side panel. Each row
              has Edit + Clear actions for in-place tweaking. */}
          <LiveExtrasPanel
            extra={form.extra}
            setExtra={setExtra}
            clearExtra={clearExtra}
          />

          {/* γ-3-A — Surcharges panel.
              γ-3-G — Gated on the `pricing` flag (handlers see this as
              read-only). Surcharges affect deal pricing so they live in
              the Árazás scope. */}
          <LockedFieldset
            disabled={!editScope.flags.surcharges}
            reason="Read-only for the Szerkesztés scope — only the Árazó can edit surcharges"
            testid="lr-surcharges-scope-lock"
          >
            <Panel title="Surcharges">
              <SurchargesEditor value={form.surcharges} onChange={(v) => set("surcharges", v)} locale="en" customerTenantId={form.customer_tenant_id} />
            </Panel>
          </LockedFieldset>

          {/* γ-3-C — Tender linking. Only render for tender requests (or when the
              user already has linked metadata on a legacy non-tender record).
              γ-3-G — Tender linking is a handler-scope concern. */}
          {(form.request_type === "tender" || form.linked_tender_id || form.linked_tender_external_ref) && (
            <LockedFieldset
              disabled={!editScope.flags.tender}
              reason="Read-only for the Árazás scope — tender linking is set by the Handler"
              testid="lr-tender-link-scope-lock"
            >
              <Panel title="Tender linking">
                <TenderLinkPicker
                  value={{
                    id: form.linked_tender_id,
                    external_ref: form.linked_tender_external_ref,
                    note: form.linked_tender_note,
                  }}
                  onChange={(v) => {
                    set("linked_tender_id", v.id || "");
                    set("linked_tender_external_ref", v.external_ref || "");
                    set("linked_tender_note", v.note || "");
                  }}
                />
              </Panel>
            </LockedFieldset>
          )}

          {/* γ-3-A — KPI flags */}
          <LockedFieldset
            disabled={!editScope.flags.kpi}
            reason="Read-only for the Árazás scope — KPI flags are set by the Handler"
            testid="lr-kpi-scope-lock"
          >
            <Panel title="KPI flags">
              <Toggle label="Vehicle delay (counts toward Vehicle delays KPI)" checked={form.vehicle_delay_flag} onChange={(v) => set("vehicle_delay_flag", v)} testid="lr-vehicle-delay-flag" />
            </Panel>
          </LockedFieldset>
        </div>

        {/* RIGHT — searchable parameter panel */}
        <ParameterPanel extra={form.extra} setExtra={setExtra} clearExtra={clearExtra} me={me} editScope={editScope} dealStage={dealStage} />
      </div>
    </div>
  );
}

// ─────────────────────── Completeness meter (iter85b E12) ───────────────────────
//
// Counts how many of the side-panel parameters the user has filled and
// renders a compact X/Total badge + a per-group sparkline. Drives the user
// gently toward fuller intake without nagging. Re-renders whenever the
// `extra` map changes.

const CompletenessMeter = ({ extra }) => {
  // γ-3-H follow-up — locale-aware group labels for the sparkline + tooltip.
  // Short codes (PARAM_GROUP_SHORT) fit the 360px side panel; the title
  // tooltip surfaces the full localized name so "Cargo / Áru / Fracht" reads
  // unambiguously on hover.
  const { locale } = useT();
  const groupLabels = PARAM_GROUP_LABELS_I18N[locale] || PARAM_GROUP_LABELS_I18N.en;
  const { total, filled, byGroup } = useMemo(() => {
    const grpInit = PARAM_GROUPS.reduce((acc, g) => ({ ...acc, [g]: { total: 0, filled: 0 } }), {});
    PARAMS.forEach((p) => {
      grpInit[p.group].total += 1;
      if (isFilled(extra[p.k])) grpInit[p.group].filled += 1;
    });
    const total = PARAMS.length;
    const filled = PARAMS.reduce((n, p) => n + (isFilled(extra[p.k]) ? 1 : 0), 0);
    return { total, filled, byGroup: grpInit };
  }, [extra]);
  const pct = total === 0 ? 0 : Math.round((filled / total) * 100);
  // Pick the bar tint based on coverage: red < 25% → amber < 60% → green.
  const tint = pct < 25 ? "#b22a29" : pct < 60 ? "#b7770d" : "#22a35a";
  return (
    <div className="px-3 py-2.5" style={{ borderBottom: "1px solid var(--border-strong, rgba(0,0,0,0.10))", background: "var(--bg-elevated, rgba(0,0,0,0.02))" }} data-testid="logistics-completeness-meter">
      <div className="flex items-baseline justify-between mb-1.5">
        <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 11, color: "var(--text, #0d1220)", letterSpacing: "0.3px" }}>
          Smart intake
        </span>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: tint, fontWeight: 700 }} data-testid="logistics-completeness-count">
          {filled}/{total} · {pct}%
        </span>
      </div>
      <div style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 18 }} data-testid="logistics-completeness-sparkline">
        {PARAM_GROUPS.map((g) => {
          const grp = byGroup[g];
          const gPct = grp.total === 0 ? 0 : grp.filled / grp.total;
          const h = Math.max(2, Math.round(gPct * 18));
          const fullLabel = groupLabels[g] || g;
          return (
            <div
              key={g}
              title={`${fullLabel}: ${grp.filled}/${grp.total}`}
              style={{ flex: 1, height: h, background: gPct === 0 ? "var(--border-strong, rgba(0,0,0,0.10))" : tint, borderRadius: 1, opacity: gPct === 0 ? 0.45 : (0.55 + gPct * 0.45), cursor: "help" }}
              data-testid={`logistics-completeness-bar-${g.toLowerCase()}`}
            />
          );
        })}
      </div>
      <div className="flex justify-between mt-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: 8.5, color: "var(--text-dimmer, #6b7388)", letterSpacing: "0.2px" }}>
        {PARAM_GROUPS.map((g) => {
          const fullLabel = groupLabels[g] || g;
          const grp = byGroup[g];
          return (
            <span
              key={g}
              title={`${fullLabel} — ${grp.filled}/${grp.total} filled`}
              style={{ flex: 1, textAlign: "center", cursor: "help", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "0 1px" }}
              data-testid={`logistics-completeness-label-${g.toLowerCase()}`}
            >
              {PARAM_GROUP_SHORT[g] || g.slice(0, 5)}
            </span>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────── Parameter side panel ───────────────────────

// ─────────────── iter95b — Live extras surface inside editor ─────────────────
//
// Renders ALL filled `form.extra` values grouped by PARAMS.group so the user
// can see what they've already added via the right-side popover BEFORE saving.
// Empty groups are hidden so the panel only takes space when there's something
// to show. Each row has Edit + Clear actions for fast in-place tweaks.

// γ-3-D refactor — PARAM_BY_KEY is imported from ./logistics/editor/params (above).

const _fmtExtraValue = (p, v) => {
  if (v == null || v === "") return "—";
  if (p?.type === "bool") return v ? "Yes" : "No";
  if (p?.type === "date") return String(v).slice(0, 10);
  if (p?.type === "datetime") return String(v).slice(0, 16).replace("T", " ");
  if (Array.isArray(v)) {
    if (p?.type === "json") return `[${v.length} item${v.length === 1 ? "" : "s"}]`;
    return v.join(", ");
  }
  if (typeof v === "object") {
    // G.3b/c — show 1-line summary of named keys.
    const keys = Object.keys(v).filter((k) => v[k] != null && v[k] !== "");
    if (keys.length === 0) return "—";
    const head = keys.slice(0, 2).map((k) => `${k}: ${String(v[k]).slice(0, 24)}`).join(" · ");
    return keys.length > 2 ? `${head} · +${keys.length - 2}` : head;
  }
  return String(v);
};

const LiveExtrasPanel = ({ extra, setExtra, clearExtra }) => {
  // γ-3-H follow-up — localized group headings (matches the right-panel
  // headers + CompletenessMeter tooltips). HU/DE users see their native
  // language in this "Filled parameters (live)" surface too.
  const { locale } = useT();
  const groupLabels = PARAM_GROUP_LABELS_I18N[locale] || PARAM_GROUP_LABELS_I18N.en;
  // Group filled keys by PARAMS.group while preserving the PARAMS order
  // (so the row order matches the right-side panel).
  const filledByGroup = {};
  PARAMS.forEach((p) => {
    if (isFilled(extra[p.k])) {
      (filledByGroup[p.group] = filledByGroup[p.group] || []).push(p);
    }
  });
  const groupsToShow = PARAM_GROUPS.filter((g) => filledByGroup[g] && filledByGroup[g].length > 0);
  if (groupsToShow.length === 0) {
    return (
      <Panel title="Filled parameters (live)">
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-dimmer, #6b7388)" }} data-testid="lr-live-extras-empty">
          No parameters added from the side panel yet. Click any item on the right to start filling them — values will appear here immediately.
        </div>
      </Panel>
    );
  }
  return (
    <Panel title="Filled parameters (live)">
      <div data-testid="lr-live-extras">
        {groupsToShow.map((g) => (
          <div key={g} className="mb-2" data-testid={`lr-live-extras-group-${g}`}>
            <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 10, color: "#185FA5", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 4 }}>
              {groupLabels[g] || g} ({filledByGroup[g].length})
            </div>
            {filledByGroup[g].map((p) => {
              const v = extra[p.k];
              return (
                <div
                  key={p.k}
                  className="flex items-center gap-2 py-1 px-2 rounded"
                  style={{ background: "rgba(24,95,165,0.05)", borderBottom: "1px dashed rgba(0,0,0,0.06)" }}
                  data-testid={`lr-live-extras-row-${p.k}`}
                >
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer, #6b7388)", letterSpacing: "0.3px", width: "44%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.label}
                  </span>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, color: "var(--text, #0d1220)", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }} data-testid={`lr-live-extras-value-${p.k}`}>
                    {_fmtExtraValue(p, v)}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      // Scroll to the side-panel item + open its popover by clicking it
                      const target = document.querySelector(`[data-testid="logistics-param-item-${p.k}"]`);
                      if (target) {
                        target.scrollIntoView({ behavior: "smooth", block: "center" });
                        target.click();
                      }
                    }}
                    className="px-1.5 py-0.5 rounded"
                    style={{ background: "transparent", border: "1px solid #185FA5", color: "#185FA5", fontFamily: "'DM Mono', monospace", fontSize: 9, fontWeight: 700, cursor: "pointer" }}
                    data-testid={`lr-live-extras-edit-${p.k}`}
                    title="Edit"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => clearExtra(p.k)}
                    className="px-1.5 py-0.5 rounded"
                    style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.45)", color: "#b22a29", fontFamily: "'DM Mono', monospace", fontSize: 9, fontWeight: 700, cursor: "pointer" }}
                    data-testid={`lr-live-extras-clear-${p.k}`}
                    title="Clear"
                  >
                    Clear
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </Panel>
  );
};



const ParameterPanel = ({ extra, setExtra, clearExtra, me, editScope, dealStage }) => {
  const [search, setSearch] = useState("");
  const [openKey, setOpenKey] = useState(null);
  // γ-3-H follow-up — locale-aware group headers ("IDENTIFICATION" / "AZONOSÍTÁS"
  // / "IDENTIFIKATION") using the same map that drives the CompletenessMeter
  // tooltips. HU and DE users no longer see English headers in their UI.
  const { locale } = useT();
  const groupLabels = PARAM_GROUP_LABELS_I18N[locale] || PARAM_GROUP_LABELS_I18N.en;
  // γ-3-G — Anchor element ref map so the portalled Popover can measure the
  // trigger row and position itself against the viewport instead of being
  // clipped by the side-panel's `overflow-y-auto` container (issue #1).
  const anchorsRef = useRef({});
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return PARAMS;
    return PARAMS.filter((p) => p.label.toLowerCase().includes(s) || p.k.toLowerCase().includes(s));
  }, [search]);
  const byGroup = useMemo(() => {
    const m = {};
    filtered.forEach((p) => { (m[p.group] = m[p.group] || []).push(p); });
    return m;
  }, [filtered]);

  const openParam = (k) => {
    setOpenKey((cur) => (cur === k ? null : k));
  };

  return (
    <div className="rounded sticky top-4 self-start" style={{ background: "var(--bg-surface, #ffffff)", border: "1px solid var(--border-strong, rgba(0,0,0,0.12))", maxHeight: "calc(100vh - 96px)", display: "flex", flexDirection: "column" }} data-testid="logistics-param-panel">
      <CompletenessMeter extra={extra} />
      <div className="px-3 py-3" style={{ borderBottom: "1px solid var(--border-strong, rgba(0,0,0,0.10))" }}>
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, marginBottom: 6 }}>All parameters</div>
        <div className="flex items-center gap-2 px-2 py-1.5 rounded" style={{ background: "var(--bg, #f6f7fb)", border: "1px solid var(--border-strong, rgba(0,0,0,0.10))" }}>
          <Search size={13} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search field…" className="flex-1 bg-transparent outline-none" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }} data-testid="logistics-param-search" />
        </div>
      </div>
      <div className="overflow-y-auto flex-1" data-testid="logistics-param-list">
        {PARAM_GROUPS.map((g) => {
          const items = byGroup[g] || [];
          if (!items.length) return null;
          return (
            <div key={g}>
              <div className="px-3 py-1.5" style={{ background: "var(--bg, #f6f7fb)", fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388", letterSpacing: "1.2px", textTransform: "uppercase" }} data-testid={`logistics-param-group-header-${g.toLowerCase()}`}>{groupLabels[g] || g}</div>
              {items.map((p) => {
                const filled = isFilled(extra[p.k]);
                // γ-3-G — Field-level write permission. Locks pricing fields
                // for handlers, route/cargo for pricers, and ALL execution
                // fields outside `executed` stage.
                const writable = editScope ? canEditParamKey(editScope, dealStage, p.k) : true;
                const lockedReason = !writable
                  ? (FIELD_GROUPS_EXECUTION.has(p.k)
                      ? `Only writable in 'Megvalósult' (Executed) stage — current: ${dealStage}`
                      : FIELD_GROUPS_PRICING.has(p.k)
                        ? "Pricing fields are write-protected — only the assigned Árazó can edit"
                        : "Route/cargo/customer fields are write-protected — only the assigned Handler can edit")
                  : null;
                return (
                  <div key={p.k} className="relative">
                    <button
                      ref={(el) => { if (el) anchorsRef.current[p.k] = el; }}
                      type="button"
                      onClick={() => writable && openParam(p.k)}
                      disabled={!writable}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
                      style={{
                        background: !writable ? "rgba(0,0,0,0.03)" : "transparent",
                        border: "none",
                        borderBottom: "1px solid var(--border-soft, rgba(0,0,0,0.05))",
                        cursor: writable ? "pointer" : "not-allowed",
                        color: !writable ? "#9198b0" : (filled ? "#0a7a3c" : "var(--text, #0d1220)"),
                        fontFamily: "'DM Mono', monospace",
                        fontSize: 11,
                        opacity: !writable ? 0.65 : 1,
                      }}
                      title={lockedReason || undefined}
                      data-testid={`logistics-param-item-${p.k}`}
                    >
                      <span className="flex-1 truncate">{p.label}</span>
                      {!writable && <Lock size={11} style={{ color: "#9198b0" }} data-testid={`logistics-param-locked-${p.k}`} />}
                      {writable && filled && <Check size={12} style={{ color: "#0a7a3c" }} data-testid={`logistics-param-filled-${p.k}`} />}
                    </button>
                    {openKey === p.k && writable && (
                      <PortalPopover
                        param={p}
                        anchorEl={anchorsRef.current[p.k]}
                        value={extra[p.k]}
                        onSave={(v) => {
                          if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) {
                            clearExtra(p.k);
                          } else {
                            setExtra(p.k, v);
                          }
                          setOpenKey(null);
                        }}
                        onClear={() => { clearExtra(p.k); setOpenKey(null); }}
                        onClose={() => setOpenKey(null)}
                        me={me}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────── Popover (portalled — γ-3-G fix) ───────────────────────
//
// Issue #1 from the γ-3-G bug batch: the side-panel parameter popover was
// rendered inside the right-side `<div className="overflow-y-auto">` which
// clipped both axes (CSS spec: an `overflow-y:auto` ancestor also clips
// overflow-x for any descendant). Native <select> drop-downs and the wider
// 320-px popover panel both got cut off — pricer/forwarder fields were
// effectively unsettable from the side panel.
//
// Fix: render the popover into document.body via React Portal and position
// it with `position: fixed` against the trigger row's getBoundingClientRect.
// Tracks scroll/resize so it stays anchored. Escapes the overflow container
// entirely.

const PortalPopover = ({ param, anchorEl, value, onSave, onClear, onClose, me }) => {
  const [local, setLocal] = useState(
    value ?? (
      param.type === "bool" ? false :
      param.type === "csv" ? "" :
      param.type === "json" ? "" :
      ""
    )
  );
  const [coords, setCoords] = useState(null);
  const POPOVER_WIDTH = 320;
  const VIEWPORT_MARGIN = 8;

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const reposition = () => {
      const rect = anchorEl.getBoundingClientRect();
      // Prefer below the trigger; flip above when there isn't enough room
      // (e.g. the trigger sits near the viewport bottom).
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const ESTIMATED_HEIGHT = 220;
      const placeAbove = spaceBelow < ESTIMATED_HEIGHT && spaceAbove > spaceBelow;
      // Right-align under the trigger by default, but pull it back inside
      // the viewport if there isn't enough room.
      let right = window.innerWidth - rect.right;
      if (right < VIEWPORT_MARGIN) right = VIEWPORT_MARGIN;
      const top = placeAbove ? Math.max(VIEWPORT_MARGIN, rect.top - ESTIMATED_HEIGHT - 4) : rect.bottom + 4;
      setCoords({ top, right });
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [anchorEl]);

  // Close on outside-click (skip clicks inside the popover or on the anchor).
  React.useEffect(() => {
    const onDocClick = (e) => {
      if (!anchorEl) return;
      const popEl = document.querySelector(`[data-testid="logistics-param-popover-${param.k}"]`);
      if (popEl && popEl.contains(e.target)) return;
      if (anchorEl.contains(e.target)) return;
      onClose && onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [anchorEl, param.k, onClose]);

  const commit = (v) => onSave(coerce(param, v));
  if (!coords) return null;

  const node = (
    <div
      className="rounded-md shadow-lg"
      style={{
        position: "fixed",
        top: coords.top,
        right: coords.right,
        zIndex: 9999,
        width: POPOVER_WIDTH,
        maxHeight: "calc(100vh - 16px)",
        overflow: "visible",
        background: "var(--bg-surface, #ffffff)",
        border: "1px solid var(--border-strong, rgba(0,0,0,0.15))",
        boxShadow: "0 12px 32px rgba(0,0,0,0.22)",
      }}
      data-testid={`logistics-param-popover-${param.k}`}
    >
      <div className="px-3 py-2 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border-strong, rgba(0,0,0,0.10))" }}>
        <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12 }}>{param.label}</span>
        <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#6b7388" }}><X size={13} /></button>
      </div>
      <div className="p-3">
        <PopoverInput param={param} value={local} onChange={setLocal} onCommit={commit} me={me} />
      </div>
      <div className="px-3 py-2 flex justify-between" style={{ borderTop: "1px solid var(--border-strong, rgba(0,0,0,0.08))" }}>
        <button type="button" onClick={onClear} className="px-2 py-1 rounded inline-flex items-center gap-1" style={btnGhost} data-testid={`logistics-param-popover-clear-${param.k}`}>Clear</button>
        <button type="button" onClick={() => onSave(coerce(param, local))} className="px-2 py-1 rounded inline-flex items-center gap-1" style={btnPrimary(false)} data-testid={`logistics-param-popover-save-${param.k}`}>Save</button>
      </div>
    </div>
  );
  return createPortal(node, document.body);
};

// γ-3-G — Scope gate wrapper. Disables the wrapped subtree visually (opacity
// + pointer-events:none) and shows a small "locked" banner explaining the
// reason. Used to gate Core/Route/Stops/KPI/Surcharges/Tender panels by the
// actor's role without changing the underlying field components.
const LockedFieldset = ({ disabled, reason, testid, children }) => {
  if (!disabled) return <>{children}</>;
  return (
    <div data-testid={testid} className="relative">
      <div
        style={{
          position: "absolute",
          top: 6,
          right: 8,
          zIndex: 2,
          background: "rgba(0,0,0,0.72)",
          color: "#ffffff",
          fontFamily: "'DM Mono', monospace",
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          padding: "3px 8px",
          borderRadius: 3,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          pointerEvents: "none",
        }}
        title={reason}
      >
        <Lock size={10} /> Read-only
      </div>
      <fieldset
        disabled
        style={{ border: "none", padding: 0, margin: 0, opacity: 0.55, pointerEvents: "none", filter: "grayscale(0.4)" }}
      >
        {children}
      </fieldset>
    </div>
  );
};

const coerce = (param, val) => {
  if (param.type === "number") return val === "" || val == null ? null : Number(val);
  if (param.type === "bool") return !!val;
  if (param.type === "csv") return typeof val === "string" ? val.split(",").map((s) => s.trim()).filter(Boolean) : (val || []);
  if (param.type === "json") {
    if (val == null || val === "") return null;
    if (typeof val !== "string") return val;
    try {
      return JSON.parse(val);
    } catch (_e) {
      return val; // commit raw; backend will validate
    }
  }
  return val;
};

const PopoverInput = ({ param, value, onChange, onCommit, me }) => {
  if (param.type === "bool") {
    return <Toggle label={param.label} checked={!!value} onChange={onChange} testid={`logistics-popover-input-${param.k}`} />;
  }
  if (param.type === "select") {
    return <Select value={value || ""} onChange={onChange} options={[["", "—"], ...param.options.map((o) => [o, o])]} testid={`logistics-popover-input-${param.k}`} />;
  }
  if (param.type === "textarea") {
    return <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()} data-testid={`logistics-popover-input-${param.k}`} />;
  }
  if (param.type === "date" || param.type === "datetime") {
    return <Input type={param.type === "date" ? "date" : "datetime-local"} value={value || ""} onChange={onChange} testid={`logistics-popover-input-${param.k}`} />;
  }
  if (param.type === "csv") {
    return <Input value={Array.isArray(value) ? value.join(", ") : value || ""} onChange={onChange} placeholder="comma, separated, values" testid={`logistics-popover-input-${param.k}`} />;
  }
  if (param.type === "json") {
    // Edit nested object / array as JSON. Pretty-print on first load,
    // round-trip-safe via coerce().
    const display = typeof value === "string" ? value : (value == null ? "" : JSON.stringify(value, null, 2));
    return (
      <textarea
        value={display}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        spellCheck={false}
        className="w-full px-2 py-1.5 rounded outline-none"
        style={{ ...inputStyle(), fontFamily: "'DM Mono', monospace", fontSize: 11 }}
        placeholder={param.placeholder || "{}"}
        data-testid={`logistics-popover-input-${param.k}`}
      />
    );
  }
  if (param.type === "colleague") {
    return <ColleaguePicker value={value} onChange={onChange} onCommit={onCommit} me={me} param={param} />;
  }
  if (param.type === "number") {
    return <Input type="number" value={value ?? ""} onChange={onChange} testid={`logistics-popover-input-${param.k}`} />;
  }
  return <Input value={value || ""} onChange={onChange} testid={`logistics-popover-input-${param.k}`} />;
};

// ─────────────────────── Colleague picker (iter85) ───────────────────────

const ColleaguePicker = ({ value, onChange, onCommit, me, param }) => {
  const [list, setList] = useState(null);
  const [invites, setInvites] = useState([]);
  const [search, setSearch] = useState("");
  const [inviteMode, setInviteMode] = useState(false);
  const [inv, setInv] = useState({ email: "", name: "", note: "" });
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  // Commit helper — writes the user_id straight to the parent extra map and
  // closes the popover so the green filled-tick renders immediately.
  const pick = (v) => { onChange(v); if (onCommit) onCommit(v); };
  React.useEffect(() => {
    axios.get(`${API}/logistics-requests/colleagues/list`).then((r) => setList(r.data.items || [])).catch(() => setList([]));
    axios.get(`${API}/logistics-requests/invite-requests/list`).then((r) => setInvites(r.data.items || [])).catch(() => {});
  }, []);
  const filtered = (list || []).filter((u) => {
    const s = search.toLowerCase();
    return !s || (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
  });
  const submitInvite = () => {
    setErr("");
    if (!inv.email.includes("@")) { setErr("Email required"); return; }
    axios.post(`${API}/logistics-requests/invite-colleague`, {
      email: inv.email.trim(), name: inv.name.trim() || null,
      role_hint: param.k.includes("arazo") ? "pricer" : "request_handler",
      note: inv.note.trim() || null,
    })
      .then((r) => {
        setOkMsg("Invite request sent — pending platform admin approval.");
        setInvites((arr) => [r.data, ...arr]);
        setInv({ email: "", name: "", note: "" });
        setInviteMode(false);
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Invite failed"));
  };
  const pendingForThisField = invites.find((iq) => iq.status === "pending" && (iq.role_hint || "").length > 0);

  if (list == null) return <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#6b7388" }}>Loading colleagues…</div>;

  return (
    <div className="space-y-2">
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search colleague…" className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()} data-testid={`logistics-colleague-search-${param.k}`} />
      <div className="max-h-44 overflow-y-auto rounded" style={{ border: "1px solid var(--border-strong, rgba(0,0,0,0.10))" }}>
        <button type="button" onClick={() => pick(null)} className="w-full text-left px-2 py-1.5" style={{ background: !value ? "rgba(24,95,165,0.10)" : "transparent", border: "none", borderBottom: "1px solid var(--border-soft, rgba(0,0,0,0.05))", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 11 }} data-testid={`logistics-colleague-none-${param.k}`}>— Unassigned —</button>
        {filtered.map((u) => (
          <button key={u.id} type="button" onClick={() => pick(u.id)} className="w-full text-left px-2 py-1.5" style={{ background: value === u.id ? "rgba(24,95,165,0.16)" : "transparent", border: "none", borderBottom: "1px solid var(--border-soft, rgba(0,0,0,0.05))", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 11 }} data-testid={`logistics-colleague-option-${u.id}`}>
            {u.name || u.email} <span style={{ color: "#6b7388" }}>· {u.role}</span>
          </button>
        ))}
      </div>
      {value === me?.id ? null : (
        <button type="button" onClick={() => pick(me?.id)} className="px-2 py-1 rounded inline-flex items-center gap-1" style={btnSecondary} data-testid={`logistics-colleague-self-${param.k}`}>Assign myself</button>
      )}
      <div className="mt-1">
        {!inviteMode && (
          <button type="button" onClick={() => setInviteMode(true)} className="px-2 py-1 rounded inline-flex items-center gap-1" style={btnSecondary} data-testid={`logistics-invite-open-${param.k}`}>
            <UserPlus size={11} /> Request to add a colleague
          </button>
        )}
        {inviteMode && (
          <div className="rounded p-2 mt-1" style={{ background: "var(--bg, #f6f7fb)", border: "1px solid var(--border-strong, rgba(0,0,0,0.10))" }} data-testid={`logistics-invite-form-${param.k}`}>
            <FieldLite label="Email *"><Input value={inv.email} onChange={(v) => setInv((s) => ({ ...s, email: v }))} testid={`logistics-invite-email-${param.k}`} /></FieldLite>
            <FieldLite label="Name"><Input value={inv.name} onChange={(v) => setInv((s) => ({ ...s, name: v }))} testid={`logistics-invite-name-${param.k}`} /></FieldLite>
            <FieldLite label="Note (optional)"><Input value={inv.note} onChange={(v) => setInv((s) => ({ ...s, note: v }))} testid={`logistics-invite-note-${param.k}`} /></FieldLite>
            {err && <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#b22a29", marginTop: 4 }} data-testid={`logistics-invite-error-${param.k}`}>{err}</div>}
            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={() => setInviteMode(false)} className="px-2 py-1 rounded" style={btnGhost}>Cancel</button>
              <button type="button" onClick={submitInvite} className="px-2 py-1 rounded" style={btnPrimary(false)} data-testid={`logistics-invite-submit-${param.k}`}>Send request</button>
            </div>
          </div>
        )}
        {okMsg && <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#0a7a3c", marginTop: 4 }} data-testid={`logistics-invite-success-${param.k}`}>{okMsg}</div>}
        {pendingForThisField && !inviteMode && (
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#f3b14a", marginTop: 4 }} data-testid={`logistics-invite-pending-${param.k}`}>
            ⏳ Pending: {pendingForThisField.email}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────── γ-3-A — Loading sides picker ───────────────────────
//
// Multi-select pill row of where loading/unloading happens at a stop.
// Data-only: filters/compatibility rules deferred.
const LoadingSidesPicker = ({ value = [], onChange, testidPrefix, locale = "en" }) => {
  const toggle = (k) => {
    const has = (value || []).includes(k);
    onChange(has ? value.filter((x) => x !== k) : [...(value || []), k]);
  };
  return (
    <div className="inline-flex flex-wrap gap-1" data-testid={`${testidPrefix}-picker`}>
      {LOADING_SIDES.map((k) => {
        const lbl = (LOADING_SIDE_LABELS[k] || {})[locale] || LOADING_SIDE_LABELS[k]?.en || k;
        const active = (value || []).includes(k);
        return (
          <button
            key={k}
            type="button"
            onClick={() => toggle(k)}
            className="px-2 py-1 rounded"
            style={{
              background: active ? "#185FA5" : "transparent",
              color: active ? "#ffffff" : "#185FA5",
              border: "1px solid #185FA5",
              fontFamily: "'DM Mono', monospace",
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.4px",
              cursor: "pointer",
            }}
            data-testid={`${testidPrefix}-${k}`}
          >
            {lbl}
          </button>
        );
      })}
    </div>
  );
};

// ─────────────────────── γ-3-A — Surcharges editor ─────────────────────────
//
// Renders the deal-level surcharges[] array as a small editable table grouped
// by category. Picks lines from the master list (useSurcharges hook) and
// auto-computes line.total = qty * unit_cost. Aggregate footer = Σ totals.

export const SurchargesEditor = ({ value = [], onChange, locale = "en", customerTenantId = "" }) => {
  const q = useSurcharges();
  const master = q.data?.items || [];
  const templatesQ = useSurchargeTemplates(customerTenantId || null);
  const createTemplate = useCreateSurchargeTemplate();
  const deleteTemplate = useDeleteSurchargeTemplate();
  const [tplPickerOpen, setTplPickerOpen] = useState(false);
  const [tplSaveOpen, setTplSaveOpen] = useState(false);
  const [tplName, setTplName] = useState("");

  const setLine = (i, key, val) => {
    const arr = [...value];
    arr[i] = { ...arr[i], [key]: val };
    if (key === "qty" || key === "unit_cost") {
      const qty = parseFloat(arr[i].qty);
      const uc  = parseFloat(arr[i].unit_cost);
      arr[i].total = Number.isFinite(qty) && Number.isFinite(uc) ? Math.round(qty * uc * 100) / 100 : arr[i].total;
    }
    onChange(arr);
  };
  const removeLine = (i) => onChange(value.filter((_, j) => j !== i));
  const addFromMaster = (m) => onChange([...(value || []), {
    id: m.id,
    name: m[`label_${locale}`] || m.label_en,
    category: m.category,
    unit: m.default_unit || "",
    qty: null, unit_cost: m.default_unit_cost ?? null, total: null,
  }]);
  const addBlank = (category) => onChange([...(value || []), {
    id: null, name: "", category, unit: "", qty: null, unit_cost: null, total: null,
  }]);
  const total = (value || []).reduce((acc, ln) => {
    const t = ln.total != null ? Number(ln.total) : (ln.qty != null && ln.unit_cost != null ? Number(ln.qty) * Number(ln.unit_cost) : 0);
    return acc + (Number.isFinite(t) ? t : 0);
  }, 0);

  const applyTemplate = (tpl) => {
    // APPEND template lines to current; never replace. Lines fully editable after.
    const appended = (tpl.lines || []).map((ln) => ({
      id: ln.id || null,
      name: ln.name || "",
      category: ln.category,
      unit: ln.unit || "",
      qty: ln.qty ?? null,
      unit_cost: ln.unit_cost ?? null,
      total: ln.total ?? null,
    }));
    onChange([...(value || []), ...appended]);
    setTplPickerOpen(false);
  };

  const onSaveTemplate = async () => {
    const nm = (tplName || "").trim();
    if (!nm) return;
    if ((value || []).length === 0) return;
    try {
      await createTemplate.mutateAsync({
        name: nm,
        customer_tenant_id: customerTenantId || null,
        lines: value.map((ln) => ({
          id: ln.id || null,
          name: ln.name || "",
          category: ln.category,
          unit: ln.unit || null,
          qty: ln.qty,
          unit_cost: ln.unit_cost,
          total: ln.total,
        })),
      });
      setTplSaveOpen(false);
      setTplName("");
    } catch (_e) {
      // toast is owned by parent; we silently fail here
    }
  };

  const templates = templatesQ.data?.items || [];

  return (
    <div data-testid="lr-surcharges-editor">
      {/* I.2 — Template toolbar (Apply / Save). Strictly optional, manual. */}
      <div className="flex items-center gap-2 mb-2 pb-2" style={{ borderBottom: "1px dashed var(--border-soft, rgba(0,0,0,0.10))" }} data-testid="lr-surcharge-templates-bar">
        <button
          type="button"
          onClick={() => setTplPickerOpen((v) => !v)}
          className="px-2 py-1 rounded inline-flex items-center gap-1"
          style={{ background: "rgba(24,95,165,0.10)", border: "1px solid rgba(24,95,165,0.40)", color: "#185FA5", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.4px", cursor: "pointer", textTransform: "uppercase" }}
          data-testid="lr-surcharge-templates-apply-btn"
          disabled={templates.length === 0}
          title={templates.length === 0 ? "No saved templates for this customer yet" : "Append a saved bundle"}
        >
          + Apply template ({templates.length})
        </button>
        <button
          type="button"
          onClick={() => setTplSaveOpen((v) => !v)}
          className="px-2 py-1 rounded inline-flex items-center gap-1"
          style={{ background: "transparent", border: "1px solid rgba(0,0,0,0.20)", color: "var(--text, #1a1a1a)", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.4px", cursor: "pointer", textTransform: "uppercase" }}
          data-testid="lr-surcharge-templates-save-btn"
          disabled={(value || []).length === 0}
          title={(value || []).length === 0 ? "Add at least one surcharge line first" : "Save current surcharges as a template for this customer"}
        >
          Save as template…
        </button>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--text-dimmer, #6b7388)", marginLeft: "auto", letterSpacing: "0.4px" }}>
          {customerTenantId ? `Scope: customer ${customerTenantId.substring(0, 8)}…` : "Scope: all customers (baseline)"}
        </span>
      </div>

      {tplPickerOpen && templates.length > 0 && (
        <div className="rounded p-2 mb-2" style={{ background: "rgba(24,95,165,0.04)", border: "1px solid rgba(24,95,165,0.30)" }} data-testid="lr-surcharge-templates-picker">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-2 p-1.5 rounded" style={{ background: "rgba(255,255,255,0.5)" }} data-testid={`lr-surcharge-template-${t.id}`}>
              <button type="button" onClick={() => applyTemplate(t)} className="text-left flex-1" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0 }} data-testid={`lr-surcharge-template-apply-${t.id}`}>
                <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12 }}>{t.name}</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388" }}>{t.lines.length} line{t.lines.length === 1 ? "" : "s"} {t.customer_tenant_id ? "" : "· baseline"}</div>
              </button>
              <button type="button" onClick={() => { if (window.confirm(`Delete template "${t.name}"?`)) deleteTemplate.mutate(t.id); }} className="p-1 rounded" style={{ background: "transparent", border: "1px solid rgba(226,75,74,0.40)", color: "#b22a29", cursor: "pointer" }} data-testid={`lr-surcharge-template-delete-${t.id}`}>
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {tplSaveOpen && (
        <div className="rounded p-2 mb-2" style={{ background: "rgba(10,135,84,0.04)", border: "1px solid rgba(10,135,84,0.30)" }} data-testid="lr-surcharge-templates-save-form">
          <div className="flex items-center gap-2">
            <Input value={tplName} onChange={setTplName} testid="lr-surcharge-templates-save-name" placeholder="Template name (e.g. BMW import EXW)" />
            <button type="button" onClick={onSaveTemplate} className="px-3 py-1.5 rounded" style={{ background: "#0a8754", border: "none", color: "#fff", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.4px", cursor: "pointer", textTransform: "uppercase" }} data-testid="lr-surcharge-templates-save-submit">Save</button>
            <button type="button" onClick={() => { setTplSaveOpen(false); setTplName(""); }} className="px-2 py-1 rounded" style={{ background: "transparent", border: "1px solid rgba(0,0,0,0.20)", cursor: "pointer" }}><X size={11} /></button>
          </div>
        </div>
      )}

      {SURCHARGE_CATEGORIES.map((cat) => {
        const tint = SURCHARGE_CATEGORY_TINT[cat];
        const catLabel = (SURCHARGE_CATEGORY_LABELS[cat] || {})[locale] || cat;
        const lines = (value || []).map((ln, i) => ({ ln, i })).filter(({ ln }) => ln.category === cat);
        const catMaster = master.filter((m) => m.category === cat);
        return (
          <div key={cat} className="mb-3" data-testid={`lr-surcharges-group-${cat}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 9999, background: tint }} aria-hidden="true" />
              <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 11, color: tint, letterSpacing: "0.3px", textTransform: "uppercase" }}>{catLabel}</span>
              <select
                className="px-2 py-1 rounded outline-none"
                style={{ ...inputStyle(), marginLeft: "auto", minWidth: 200 }}
                value=""
                onChange={(e) => {
                  if (e.target.value === "__blank__") addBlank(cat);
                  else { const m = catMaster.find((x) => x.id === e.target.value); if (m) addFromMaster(m); }
                  e.target.value = "";
                }}
                data-testid={`lr-surcharges-add-${cat}`}
              >
                <option value="">+ Add from master…</option>
                {catMaster.map((m) => (
                  <option key={m.id} value={m.id}>{m[`label_${locale}`] || m.label_en}{m.default_unit ? ` · ${m.default_unit}` : ""}</option>
                ))}
                <option value="__blank__">… or blank line</option>
              </select>
            </div>
            {lines.length === 0 && (
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer, #6b7388)", padding: "4px 8px" }}>no lines</div>
            )}
            {lines.map(({ ln, i }) => (
              <div key={i} className="grid grid-cols-[2.2fr_0.8fr_0.6fr_0.8fr_0.8fr_auto] gap-1.5 items-center mb-1" data-testid={`lr-surcharge-row-${i}`}>
                <Input value={ln.name || ""} onChange={(v) => setLine(i, "name", v)} testid={`lr-surcharge-row-${i}-name`} placeholder="Name" />
                <Input value={ln.unit || ""} onChange={(v) => setLine(i, "unit", v)} testid={`lr-surcharge-row-${i}-unit`} placeholder="unit" />
                <Input type="number" value={ln.qty ?? ""} onChange={(v) => setLine(i, "qty", v === "" ? null : Number(v))} testid={`lr-surcharge-row-${i}-qty`} placeholder="qty" />
                <Input type="number" value={ln.unit_cost ?? ""} onChange={(v) => setLine(i, "unit_cost", v === "" ? null : Number(v))} testid={`lr-surcharge-row-${i}-unit-cost`} placeholder="unit cost" />
                <Input type="number" value={ln.total ?? ""} onChange={(v) => setLine(i, "total", v === "" ? null : Number(v))} testid={`lr-surcharge-row-${i}-total`} placeholder="total" />
                <button type="button" onClick={() => removeLine(i)} className="px-2 py-1 rounded" style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.45)", color: "#b22a29", cursor: "pointer" }} data-testid={`lr-surcharge-row-${i}-remove`}>
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        );
      })}
      <div className="flex items-center justify-end gap-2 pt-2" style={{ borderTop: "1px dashed var(--border-soft, rgba(0,0,0,0.10))" }}>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer, #6b7388)", letterSpacing: "1px", textTransform: "uppercase" }}>Total</span>
        <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "#185FA5" }} data-testid="lr-surcharges-total">
          {total.toFixed(2)}
        </span>
      </div>
    </div>
  );
};


// ─────────────────────── γ-3-C — Tender link picker ────────────────────────
//
// Lets the user link a new tender to a previous one. Two paths:
//   1. Search past tenders in the system (any outcome — won, lost, open) and
//      pick one → sets linked_tender_id.
//   2. Tender is NOT in the system yet → free-text external ref + optional note.
export const TenderLinkPicker = ({ value = {}, onChange }) => {
  const [q, setQ] = useState("");
  const tendersQ = useTendersForLinking(q);
  const items = tendersQ.data?.items || [];
  // pick() flips value.id which immediately causes the conditional below to
  // render the picked-chip and unmount the search input — so we DON'T also
  // call setQ("") in the same tick (that was racing the state update and
  // dropping the linked_tender_id assignment under React 18).
  const pick = (it) => onChange({ id: it.id, external_ref: "", note: value.note || "" });
  const clearPick = () => {
    onChange({ id: "", external_ref: value.external_ref || "", note: value.note || "" });
    setQ("");
  };
  return (
    <div data-testid="lr-tender-link-picker">
      {value.id ? (
        // Picked: render a chip with Clear. NOT wrapped in <label> so the
        // Clear <button> click is not intercepted by label-input forwarding.
        <div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388", letterSpacing: "0.5px", marginBottom: 2 }}>Linked tender (in system)</div>
          <div className="flex items-center gap-2 py-1" data-testid="lr-tender-link-picked">
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#185FA5", padding: "2px 6px", background: "rgba(24,95,165,0.10)", borderRadius: 3 }}>{value.id}</span>
            <button type="button" onClick={clearPick} className="px-2 py-0.5 rounded" style={{ background: "transparent", border: "1px solid rgba(0,0,0,0.15)", color: "#6b7388", fontFamily: "'DM Mono', monospace", fontSize: 9, cursor: "pointer" }} data-testid="lr-tender-link-clear">
              Clear
            </button>
          </div>
        </div>
      ) : (
        // Empty: render the search input inside FieldLite, but the option
        // list as a SIBLING of FieldLite (NOT inside the <label>) — labels
        // forward click events to the wrapped <input> which was swallowing
        // the option <button> click in the previous implementation.
        <div>
          <FieldLite label="Linked tender (in system)">
            <Input value={q} onChange={(v) => setQ(v)} placeholder="Search by deal name / customer / id" testid="lr-tender-link-search" />
          </FieldLite>
          {q && items.length > 0 && (
            <div className="mt-1 max-h-40 overflow-y-auto rounded" style={{ border: "1px solid var(--border-strong, rgba(0,0,0,0.10))" }}>
              {items.slice(0, 12).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(it)}
                  className="block w-full text-left px-2 py-1 cursor-pointer"
                  style={{ background: "transparent", border: "none", borderBottom: "1px dashed rgba(0,0,0,0.06)", fontFamily: "'DM Mono', monospace", fontSize: 10 }}
                  data-testid={`lr-tender-link-option-${it.id}`}
                >
                  <strong>{it.deal_name || it.id}</strong>
                  {it.customer_name ? ` · ${it.customer_name}` : ""}
                  {it.deal_stage ? <span style={{ color: "#6b7388" }}> · {it.deal_stage}</span> : null}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {!value.id && (
        <FieldLite label="External reference (when not in system)">
          <Input value={value.external_ref || ""} onChange={(v) => onChange({ id: "", external_ref: v, note: value.note || "" })} placeholder="e.g. BMW-2025-Q3" testid="lr-tender-link-external-ref" />
        </FieldLite>
      )}
      <FieldLite label="Linking note (optional)">
        <Input value={value.note || ""} onChange={(v) => onChange({ id: value.id || "", external_ref: value.external_ref || "", note: v })} placeholder="e.g. Same lane as 2025; expect ±5% rate." testid="lr-tender-link-note" />
      </FieldLite>
    </div>
  );
};



// ─────────────────────── Bits & shared style ───────────────────────

const Panel = ({ title, children }) => (
  <div className="rounded p-4" style={{ background: "var(--bg-surface, #ffffff)", border: "1px solid var(--border-strong, rgba(0,0,0,0.10))" }}>
    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{title}</div>
    <div className="space-y-2">{children}</div>
  </div>
);

const FieldLite = ({ label, children }) => (
  <label className="block">
    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388", letterSpacing: "0.5px", marginBottom: 2 }}>{label}</div>
    {children}
  </label>
);

const inputStyle = () => ({ background: "var(--bg-surface, #ffffff)", border: "1px solid var(--border-strong, rgba(0,0,0,0.18))", color: "var(--text, #0d1220)", fontFamily: "'DM Mono', monospace", fontSize: 11 });

const Input = ({ value, onChange, type = "text", testid, placeholder }) => (
  <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()} data-testid={testid} />
);

const Select = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()} data-testid={testid}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="inline-flex items-center gap-2 px-2 py-1 rounded" style={{ background: checked ? "rgba(24,95,165,0.12)" : "rgba(0,0,0,0.03)", border: `1px solid ${checked ? "rgba(24,95,165,0.45)" : "rgba(0,0,0,0.10)"}`, fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: "pointer", color: checked ? "#0d3a82" : "var(--text, #0d1220)" }}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
    {label}
  </label>
);

// I.2 — Payment term selector. Default-inherits from customer_masters when
// a customer_tenant_id is provided. Free "Custom" entry available.
const PAYMENT_TERM_OPTIONS = ["NET7", "NET14", "NET30", "NET45", "NET60"];
const PaymentTermSelect = ({ value, onChange, customerTenantId }) => {
  const [custom, setCustom] = React.useState(false);
  const [defaultedFrom, setDefaultedFrom] = React.useState(null);

  React.useEffect(() => {
    // Default-inherit from customer_masters when the operator hasn't set
    // a payment term yet AND a customer_tenant_id is selected.
    if (!customerTenantId) return;
    if ((value || "").trim()) return;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    import("axios").then(({ default: ax }) => {
      ax.get(`${API}/customer-masters`).then((r) => {
        const d = r.data;
        const arr = Array.isArray(d) ? d : (d?.items || []);
        const hit = arr.find((m) => m.customer_tenant_id === customerTenantId);
        const term = hit?.payment_terms;
        if (term && !value) {
          onChange(term);
          setDefaultedFrom(term);
        }
      }).catch(() => {});
    });
  }, [customerTenantId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const isPreset = PAYMENT_TERM_OPTIONS.includes(value);
  const showCustom = custom || (!!value && !isPreset);

  return (
    <div className="flex items-center gap-1.5">
      <select
        value={isPreset ? value : (showCustom ? "__custom__" : "")}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "__custom__") { setCustom(true); return; }
          setCustom(false);
          onChange(v);
        }}
        className="px-2 py-1.5 rounded outline-none"
        style={inputStyle()}
        data-testid="lr-payment-term-select"
      >
        <option value="">— Select payment term —</option>
        {PAYMENT_TERM_OPTIONS.map((p) => <option key={p} value={p}>{p.replace("NET", "Net ") + " days"}</option>)}
        <option value="__custom__">Custom…</option>
      </select>
      {showCustom && (
        <Input value={isPreset ? "" : value} onChange={onChange} placeholder="e.g. EOM+30" testid="lr-payment-term-custom" />
      )}
      {defaultedFrom && value === defaultedFrom && (
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#0a8754", letterSpacing: "0.4px", whiteSpace: "nowrap" }} data-testid="lr-payment-term-inherited-pill">FROM CUSTOMER</span>
      )}
    </div>
  );
};




// iter86b — Vehicle type grouped dropdown.
//
// Replaces the free-text Autótípus field with an admin-curated, EN/DE/HU
// localized, grouped picker driven by /vehicle-types/list. The stored value
// is the picked entry's localized label (so legacy free-text rows still
// render correctly + the value matches what the admin sees).
const VEHICLE_CAT_LABEL = {
  road_curtainside: { en: "Road · curtainside / standard", de: "Straße · Plane / Standard", hu: "Közúti · ponyvás / standard" },
  road_groupage:    { en: "Road · groupage / smaller",      de: "Straße · Sammelgut / klein",  hu: "Közúti · gyűjtő / kisebb" },
  road_temperature: { en: "Road · temperature / special",   de: "Straße · Temperatur / Spezial", hu: "Közúti · hűtős / speciális" },
  road_special:     { en: "Road · special cargo",           de: "Straße · Sonderladung",       hu: "Közúti · különleges áru" },
  container:        { en: "Container / intermodal",         de: "Container / intermodal",      hu: "Konténer / intermodális" },
  sea:              { en: "Sea",                            de: "Seefracht",                   hu: "Tengeri" },
  air:              { en: "Air",                            de: "Luftfracht",                  hu: "Légi" },
  rail:             { en: "Rail",                           de: "Bahn",                        hu: "Vasút" },
  combined:         { en: "Combined",                       de: "Kombiniert",                  hu: "Kombinált" },
};

const VehicleTypeSelect = ({ value, onChange }) => {
  const q = useVehicleTypes();
  const { locale } = useT();
  const items = q.data?.items || [];
  const labelFor = (it) => (it[`label_${locale}`] || it.label_en || "").trim();
  const groups = useMemo(() => {
    const m = {};
    items.forEach((it) => { (m[it.category] = m[it.category] || []).push(it); });
    return m;
  }, [items]);
  const knownLabels = useMemo(
    () => new Set(items.flatMap((it) => [it.label_en, it.label_de, it.label_hu])),
    [items]
  );
  const isLegacy = value && !knownLabels.has(value);
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded outline-none"
      style={inputStyle()}
      data-testid="lr-autotipus"
    >
      <option value="">—</option>
      {isLegacy && <option value={value}>{value} (legacy)</option>}
      {Object.keys(groups).sort().map((cat) => {
        const meta = VEHICLE_CAT_LABEL[cat] || { en: cat, de: cat, hu: cat };
        return (
          <optgroup key={cat} label={meta[locale] || meta.en}>
            {groups[cat].map((it) => (
              <option key={it.id} value={labelFor(it)} data-testid={`lr-autotipus-option-${it.id}`} label={`${labelFor(it)} · ${it.transport_mode}`}>
                {`${labelFor(it)} · ${it.transport_mode}`}
              </option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
};

const CategorySelect = ({ value, onChange, cats, onCreate }) => {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  return (
    <div className="flex gap-1 items-center">
      <Select value={value} onChange={onChange} options={[["", "—"], ...cats.map((c) => [c.id, c.name])]} testid="lr-customer-category" />
      {!adding ? (
        <button type="button" onClick={() => setAdding(true)} className="px-2 py-1 rounded" style={btnSecondary} data-testid="lr-new-category-open">+ New</button>
      ) : (
        <>
          <Input value={name} onChange={setName} placeholder="name" testid="lr-new-category-name" />
          <button type="button" onClick={() => { if (name.trim()) { onCreate(name.trim()); setName(""); setAdding(false); } }} className="px-2 py-1 rounded" style={btnPrimary(false)} data-testid="lr-new-category-add">Add</button>
        </>
      )}
    </div>
  );
};

const formatErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return `${d.message}: ${(d.missing || []).join(", ")}`;
  return "Save failed";
};

// High-contrast buttons (iter85 fix — was washed-out in light mode).
const btnPrimary = (busy) => ({
  background: "#185FA5",
  border: "1px solid #0d3f78",
  color: "#ffffff",
  fontFamily: "Syne, sans-serif",
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "0.5px",
  cursor: busy ? "not-allowed" : "pointer",
  opacity: busy ? 0.6 : 1,
  textShadow: "0 1px 0 rgba(0,0,0,0.18)",
});
const btnSecondary = {
  background: "#ffffff",
  border: "1px solid #185FA5",
  color: "#185FA5",
  fontFamily: "'DM Mono', monospace",
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: "0.4px",
  cursor: "pointer",
};
const btnGhost = {
  background: "transparent",
  border: "1px solid rgba(0,0,0,0.20)",
  color: "var(--text, #0d1220)",
  fontFamily: "'DM Mono', monospace",
  fontWeight: 600,
  fontSize: 11,
  letterSpacing: "0.4px",
  cursor: "pointer",
};
